// src/components/customer/CustomerDashboard.js
import { useMemo } from 'react';
import { Package, DollarSign, Clock, TrendingUp } from 'lucide-react';

export function CustomerDashboard({ customer, orders }) {
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const thisMonthOrders = orders.filter(o => {
      const date = o.createdAt?.toDate?.() || new Date(o.createdAt);
      return date >= thisMonth;
    });

    const pendingOrders = orders.filter(o => 
      o.status !== 'Delivered' && o.status !== 'Cancelled'
    );

    const totalSpent = orders.reduce((sum, o) => sum + (o.subtotal || 0), 0);
    const thisMonthSpent = thisMonthOrders.reduce((sum, o) => sum + (o.subtotal || 0), 0);

    return {
      totalOrders: orders.length,
      thisMonthOrders: thisMonthOrders.length,
      pendingOrders: pendingOrders.length,
      totalSpent,
      thisMonthSpent
    };
  }, [orders]);

  const recentOrders = orders.slice(0, 5);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Welcome, {customer?.companyName}</h2>
        <p className="text-gray-500">Your wholesale discount: <span className="font-semibold text-green-600">{customer?.tierDiscount || 0}% off</span></p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Orders</p>
              <p className="text-2xl font-bold">{stats.totalOrders}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Package className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold">{stats.pendingOrders}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <Clock className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">This Month</p>
              <p className="text-2xl font-bold">${stats.thisMonthSpent.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <TrendingUp className="text-green-600" size={24} />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">{stats.thisMonthOrders} orders</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Spent</p>
              <p className="text-2xl font-bold">${stats.totalSpent.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <DollarSign className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold mb-4">Recent Orders</h3>
        {recentOrders.length === 0 ? (
          <p className="text-gray-500 text-sm">No orders yet. Start shopping!</p>
        ) : (
          <div className="space-y-3">
            {recentOrders.map(order => {
              const date = order.createdAt?.toDate?.() || new Date(order.createdAt);
              const statusColors = {
                'New': 'bg-blue-100 text-blue-800',
                'Processing': 'bg-yellow-100 text-yellow-800',
                'Filled': 'bg-purple-100 text-purple-800',
                'Shipped': 'bg-indigo-100 text-indigo-800',
                'Delivered': 'bg-green-100 text-green-800',
                'Cancelled': 'bg-red-100 text-red-800'
              };
              return (
                <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Order #{order.id.slice(-8).toUpperCase()}</p>
                    <p className="text-xs text-gray-500">{date.toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${statusColors[order.status] || 'bg-gray-100'}`}>
                      {order.status}
                    </span>
                    <span className="font-medium">${(order.total || order.subtotal)?.toFixed(2)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
